import { useEffect, useRef, useCallback } from "react";

// GAM network: 23344724145, ad unit: vlive-preroll (640x480v)
// correlator= is left blank — IMA SDK populates it automatically at runtime
const PRODUCTION_AD_TAG =
  process.env.REACT_APP_IMA_AD_TAG ||
  "https://pubads.g.doubleclick.net/gampad/ads?iu=/23344724145/video_ad_unit&description_url=https%3A%2F%2Fvlivearchive.com&tfcd=0&npa=0&sz=640x480&gdfp_req=1&unviewed_position_start=1&output=vast&env=vp&impl=s&correlator=";

// Google's sample pre-roll tag for local testing (GAM rejects requests from localhost)
const TEST_AD_TAG =
  "https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/single_preroll_skippable&sz=640x480&ciu_szs=300x250%2C728x90&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s&correlator=";

const AD_TAG_URL =
  window.location.hostname === "localhost" ? TEST_AD_TAG : PRODUCTION_AD_TAG;

const PreRollAd = ({
  onComplete,
  poster,
}: {
  onComplete: () => void;
  poster?: string;
}) => {
  const adContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const adsManagerRef = useRef<google.ima.AdsManager | null>(null);
  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const finish = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    if (adsManagerRef.current) {
      adsManagerRef.current.destroy();
      adsManagerRef.current = null;
    }
    onCompleteRef.current();
  }, []);

  useEffect(() => {
    // Ad blocker or SDK not loaded — skip to content
    if (typeof google === "undefined" || !google.ima) {
      finish();
      return;
    }

    const adContainer = adContainerRef.current;
    const video = videoRef.current;
    if (!adContainer || !video) {
      finish();
      return;
    }

    // Safety timeout: skip to content if ad hasn't finished in 15s
    const timeout = setTimeout(finish, 15000);

    const adDisplayContainer = new google.ima.AdDisplayContainer(
      adContainer,
      video
    );
    adDisplayContainer.initialize();

    const adsLoader = new google.ima.AdsLoader(adDisplayContainer);

    adsLoader.addEventListener(
      google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      (event: google.ima.AdsManagerLoadedEvent) => {
        const adsManager = event.getAdsManager(video);
        adsManagerRef.current = adsManager;

        adsManager.addEventListener(
          google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
          finish
        );
        adsManager.addEventListener(
          google.ima.AdEvent.Type.SKIPPED,
          finish
        );
        adsManager.addEventListener(
          google.ima.AdErrorEvent.Type.AD_ERROR,
          finish
        );

        try {
          adsManager.init(
            adContainer.clientWidth,
            adContainer.clientHeight,
            google.ima.ViewMode.NORMAL
          );
          adsManager.start();
        } catch (e) {
          finish();
        }
      }
    );

    adsLoader.addEventListener(
      google.ima.AdErrorEvent.Type.AD_ERROR,
      finish
    );

    const adsRequest = new google.ima.AdsRequest();
    adsRequest.adTagUrl = AD_TAG_URL;
    adsRequest.linearAdSlotWidth = adContainer.clientWidth;
    adsRequest.linearAdSlotHeight = adContainer.clientHeight;

    try {
      adsLoader.requestAds(adsRequest);
    } catch (e) {
      finish();
    }

    const handleResize = () => {
      if (adsManagerRef.current && adContainer) {
        adsManagerRef.current.resize(
          adContainer.clientWidth,
          adContainer.clientHeight,
          google.ima.ViewMode.NORMAL
        );
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", handleResize);
      if (adsManagerRef.current) {
        adsManagerRef.current.destroy();
        adsManagerRef.current = null;
      }
    };
  }, [finish]);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "16 / 9",
        background: "#000",
      }}
    >
      <video
        ref={videoRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
        poster={poster}
        muted
        playsInline
      />
      <div
        ref={adContainerRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      />
    </div>
  );
};

export default PreRollAd;
